/**
 * Programme scolaire : classes et matières, import de collections.
 * Lectures sur les routes utilisateur, suppressions sur ADMIN_URLS.content.
 *
 * Extrait de l'ancien services/api.ts (Lot 8, déplacement pur).
 */

import {
  Matiere,
  Classe,
} from '../types';
import type { ApiResponse, DataResponse } from '../types/api';
import { ADMIN_URLS } from '../config/adminApi';
import { createApiClient, withEnv } from '../lib/http';
import { AppEnvironment } from '../config/environment';

const api = createApiClient();

export class CurriculumApiService {
  /** Liste les matières de l'environnement. */
  static async getMatieres(env: AppEnvironment): Promise<Matiere[]> {
    const response = await api.get<DataResponse<Matiere[]>>(`/matieres`, withEnv(env));
    return response.data.data;
  }

  /** Lit une matière par son identifiant. */
  static async getMatiereById(env: AppEnvironment, id: string): Promise<Matiere> {
    const response = await api.get<DataResponse<Matiere>>(`/matieres/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Crée une matière. */
  static async createMatiere(env: AppEnvironment, matiere: Omit<Matiere, 'id'>): Promise<ApiResponse<Matiere>> {
    const response = await api.post<ApiResponse<Matiere>>(`/matieres`, matiere, withEnv(env));
    return response.data;
  }

  /** Met à jour une matière. */
  static async updateMatiere(env: AppEnvironment, id: string, matiere: Partial<Matiere>): Promise<ApiResponse<Matiere>> {
    const response = await api.put<ApiResponse<Matiere>>(`/matieres/${id}`, matiere, withEnv(env));
    return response.data;
  }

  /** Supprime une matière. */
  static async deleteMatiere(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/matieres/${id}`, withEnv(env));
    return response.data;
  }

  /** Liste les classes de l'environnement. */
  static async getClasses(env: AppEnvironment): Promise<Classe[]> {
    const response = await api.get<DataResponse<Classe[]>>(`/classes`, withEnv(env));
    return response.data.data;
  }

  /** Lit une classe par son identifiant. */
  static async getClasseById(env: AppEnvironment, id: string): Promise<Classe> {
    const response = await api.get<DataResponse<Classe>>(`/classes/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Crée une classe. */
  static async createClasse(env: AppEnvironment, classe: Omit<Classe, 'id'>): Promise<ApiResponse<Classe>> {
    const response = await api.post<ApiResponse<Classe>>(`/classes`, classe, withEnv(env));
    return response.data;
  }

  /** Met à jour une classe. */
  static async updateClasse(env: AppEnvironment, id: string, classe: Partial<Classe>): Promise<ApiResponse<Classe>> {
    const response = await api.put<ApiResponse<Classe>>(`/classes/${id}`, classe, withEnv(env));
    return response.data;
  }

  /** Supprime une classe. */
  static async deleteClasse(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/classes/${id}`, withEnv(env));
    return response.data;
  }
}

export default CurriculumApiService;
